import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useMutation } from '@apollo/client';
import { ChipSelect } from 'components/ads';

import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';

import { Size } from 'components/ads/chipSelect/ChipSelect';
import Label from 'components/general/Label';

import styles from './StatusDropdown.scss';
import { updateServiceRequestStatusByIdMutation } from 'graphql-queries';
import { safeConvertToUUID } from 'uuid-encoding';
import { createStatusDropdownViewModel } from './createStatusDropdownViewModel';

type PickedStatus = {
  previous: string | null | undefined;
  next: string;
};

type Props = {
  serviceRequestData: {
    serviceRequestId: string;
    status?: string | null;
    nextStatuses?: (string | null)[] | null;
  };
  isServiceRequestList?: boolean;
  refetch?: () => void;
  pillSize?: Size;
  disableEdit?: boolean;
  testId?: string;
};

export const ServiceRequestStatusDropdown = ({
  serviceRequestData,
  isServiceRequestList = false,
  refetch,
  pillSize,
  disableEdit = false,
  testId,
}: Props) => {
  const { t } = useTranslation();
  const [updateServiceRequestStatus] = useMutation(
    updateServiceRequestStatusByIdMutation
  );

  // The pick only stands in for the status it was made from. Once the data
  // moves on, drop it so a later return to that status isn't overridden.
  const [pickedStatus, setPickedStatus] = useState<PickedStatus>();
  const isPickCurrent = pickedStatus?.previous === serviceRequestData?.status;

  if (pickedStatus && !isPickCurrent) {
    setPickedStatus(undefined);
  }

  const currentStatus =
    pickedStatus && isPickCurrent
      ? pickedStatus.next
      : serviceRequestData?.status;

  const { selected, options } = createStatusDropdownViewModel(
    currentStatus,
    serviceRequestData?.nextStatuses
  );

  const updateWorkOrderUCIStatus = async (value: string) => {
    if (value === ServiceRequestStatusEnum.Cancelled) {
      try {
        await window.Alert.confirm({
          title: t('web.admin.serviceRequest.cancel.modal.header'),
          message: t('web.admin.serviceRequest.cancel.modal.action.message'),
          cancelText: t(
            'web.admin.serviceRequest.cancel.modal.backbutton.text'
          ),
          confirmText: t('web.admin.serviceRequest.cancel.modal.button.text'),
        });
        // FIXME: Log error for datadog, missing stack trace
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // user "cancelled" their cancellatiion request
        return;
      }
    }

    try {
      await updateServiceRequestStatus({
        variables: {
          id: safeConvertToUUID(serviceRequestData?.serviceRequestId),
          status: value,
          skip: !serviceRequestData?.serviceRequestId,
        },
      });
      setPickedStatus({ previous: serviceRequestData?.status, next: value });

      if (refetch) {
        await refetch();
      }

      await window.Toast.show(
        t('web.admin.serviceRequest.status.update.success')
      );
    } catch (err) {
      await window.Alert.alert({
        title: t('web.admin.serviceRequest.status.update.failure'),
        error: err,
      });
    }
  };

  return (
    <div className={styles.chipSelectContainer}>
      {!isServiceRequestList && (
        <Label mb={1} mt={0}>
          {t('web.admin.serviceRequest.status')}
        </Label>
      )}
      <ChipSelect
        testId={
          testId ?? `statusSelect-${serviceRequestData?.serviceRequestId}`
        }
        anchorClassName={styles.anchor}
        value={selected.value}
        list={options}
        size={pillSize}
        onChange={updateWorkOrderUCIStatus}
        isDisabled={disableEdit}
      />
    </div>
  );
};
